import { HostBinding, inject, input, OnDestroy, output } from '@angular/core';
import { Directive, ElementRef, AfterViewInit } from '@angular/core';
import { BaseLibComponent } from '../abstract-components/base-lib.component';

@Directive({
  selector: '[elementBounding]',
  standalone: true
})
export class ElementBoundingDirective
  extends BaseLibComponent
  implements AfterViewInit, OnDestroy
{
  protected _elementRef = inject(ElementRef);
  // protected _zone = inject(NgZone);

  boundingChange = output<unknown>();

  listenFor = input<'width' | 'height' | 'all'>('width');
  cssVarName = input<string | null | undefined>();

  @HostBinding('class.bounding-sm') get isSM(): boolean {
    return this.listenFor() !== 'height' && this.currentWidth < 640;
  }

  @HostBinding('class.bounding-md') get isMD(): boolean {
    return (
      this.listenFor() !== 'height' &&
      this.currentWidth >= 640 &&
      this.currentWidth < 1024
    );
  }

  @HostBinding('class.bounding-lg') get isLG(): boolean {
    return (
      this.listenFor() !== 'height' &&
      this.currentWidth >= 1024 &&
      this.currentWidth < 1200
    );
  }

  @HostBinding('class.bounding-xl') get isXL(): boolean {
    return this.listenFor() !== 'height' && this.currentWidth >= 1200;
  }

  currentWidth = 0;

  observer!: ResizeObserver;

  ngAfterViewInit(): void {
    if (!this._isPlatformBrowser) return;
    this.observer = new ResizeObserver(elements => {
      const element = elements[0];
      const target = element.target as HTMLDivElement;
      const cssVarName = this.cssVarName();
      switch (this.listenFor()) {
        case 'width':
          this.boundingChange.emit(element.contentRect.width);
          break;
        case 'height':
          this.boundingChange.emit(element.contentRect.height);
          break;
        case 'all':
          this.boundingChange.emit({
            ...(element.contentRect as DOMRect).toJSON(),
            clientWidth: target.clientWidth,
            clientHeight: target.clientHeight,
            offsetWidth: target.offsetWidth,
            offsetLeft: target.offsetLeft
          });
          if (cssVarName) {
            this._document.documentElement.style.setProperty(
              `--${cssVarName}-height`,
              `${target.clientHeight}px`
            );
            this._document.documentElement.style.setProperty(
              `--${cssVarName}-width`,
              `${target.clientWidth}px`
            );
            this._document.documentElement.style.setProperty(
              `--${cssVarName}-offset-left`,
              `${target.offsetLeft}px`
            );
            this._document.documentElement.style.setProperty(
              `--${cssVarName}-offset-width`,
              `${target.offsetWidth}px`
            );
          }
          break;
        default:
          this.boundingChange.emit(element.contentRect.width);
          break;
      }

      if (
        this.listenFor() !== 'height' &&
        this.currentWidth !== element.contentRect.width
      ) {
        this.currentWidth = element.contentRect.width;
        this._cdRef.detectChanges();
      }
    });

    this.observer.observe(this._elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    if (!this._isPlatformBrowser) return;
    this.observer.disconnect();
  }
}
